import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/api';
import MarcaACH from '../components/MarcaACH';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

/**
 * L'entrada a l'aplicació, amb dues portes.
 *
 * El **codi d'accés** va a l'esquerra: el dia de l'examen és el que fa servir la
 * classe sencera, amb la targeta impresa a la mà i el rellotge a punt de
 * començar. L'entrada amb **usuari i contrasenya** —les persones donades d'alta,
 * i l'instructor— és a la dreta, sencera i sense canvis.
 *
 * Van de costat i no apilades: apilades, la pàgina feia el doble d'alt que la
 * finestra i la segona porta quedava fora de la vista.
 *
 * La confirmació entre teclejar el codi i entrar no és un pas de més: és el que
 * evita que un equip que s'equivoca teclejant acabi dins de l'examen d'un altre,
 * i el que avisa que entrar tancarà la sessió que hi hagi oberta.
 */
function Login() {
  const [codi, setCodi] = useState('');
  const [comprovant, setComprovant] = useState(false);
  const [confirmacio, setConfirmacio] = useState(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, entraAmbCodi } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Si l'han tret perquè algú ha entrat amb el mateix codi en un altre
  // ordinador, s'hi diu. Un logout sense explicació enmig d'un examen sembla
  // una avaria; això té causa i sortida.
  useEffect(() => {
    if (sessionStorage.getItem('motiuSortida') === 'sessioDesplacada') {
      sessionStorage.removeItem('motiuSortida');
      toast.error(t('auth.sessioDesplacada'), { duration: 10000 });
    }
  }, [t]);

  const handleCodi = async (e) => {
    e.preventDefault();
    if (!codi.trim()) return;

    setComprovant(true);
    try {
      const response = await authService.comprovaCodi(codi);
      setConfirmacio(response.data);
    } catch (error) {
      // El 429 s'ha de distingir SEMPRE del codi dolent.
      //
      // Tota l'aula entra des d'una sola IP pública i tothom prem alhora, o
      // sigui que el límit de peticions de Traefik es pot tocar just en el pitjor
      // moment. La resposta és un 429 sense cos JSON, i sense aquesta branca
      // l'equip llegiria «aquest codi no és vàlid» amb una targeta perfectament
      // bona a la mà: aixecarien la mà tots alhora i el problema real —esperar
      // cinc segons— quedaria amagat.
      if (error.response?.status === 429) {
        toast.error(t('auth.masaPeticions'), { duration: 8000 });
      } else {
        const detall = error.response?.data?.detail;
        toast.error(typeof detall === 'string' ? detall : t('auth.codiNoValid'));
      }
    } finally {
      setComprovant(false);
    }
  };

  const handleConfirma = async () => {
    setComprovant(true);
    const desti = await entraAmbCodi(codi);
    setComprovant(false);
    if (desti) {
      navigate(desti);
    } else {
      setConfirmacio(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const success = await login(username, password);
    if (success) {
      navigate('/');
    }
    setLoading(false);
  };

  if (confirmacio) {
    return (
      <div className="form-container">
        <h2>{t('auth.confirmaEquipTitol')}</h2>
        <p className="entrada-codi__equip">{confirmacio.display_name}</p>
        <p>{t('auth.confirmaEquipPregunta')}</p>

        {confirmacio.sessio_oberta && (
          <div className="alert alert-warning">
            {t('auth.sessioJaOberta')}
          </div>
        )}

        <div className="entrada-codi__accions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirmacio(null)}
            disabled={comprovant}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirma}
            disabled={comprovant}
            autoFocus
          >
            {comprovant ? t('auth.loggingIn') : t('auth.confirmaEquipBoto')}
          </button>
        </div>
      </div>
    );
  }

  return (
    /* Dues columnes, no dues seccions apilades.
       Apilades, la pàgina feia el doble d'alt que la finestra i l'entrada amb
       contrasenya quedava sota la línia de flotació: qui hi va a corregir havia
       de desplaçar-se per trobar el seu formulari. De costat, les dues portes es
       veuen alhora i cadascú va a la seva. Per sota de 860 px tornen a apilar-se
       (vegeu App.css): en una pantalla estreta, dues columnes són dues columnes
       massa primes. */
    <div className="entrada-doble">
      {/* La marca presideix les dues portes i ocupa tota l'amplada de la
          graella: és l'única pantalla que es veu sense haver entrat, i la
          barra de dalt encara no diu de qui és l'aplicació a qui hi arriba. */}
      <div className="entrada-capcalera">
        <MarcaACH mida={48} />
        <p className="entrada-capcalera__nom">{t('nav.brandName')}</p>
      </div>

      <section className="entrada-doble__porta">
        <h2>{t('auth.entraEquipTitol')}</h2>
        <p className="entrada-doble__ajuda">{t('auth.entraEquipIntro')}</p>

        <form onSubmit={handleCodi} className="entrada-codi">
          <div className="form-group">
            <label htmlFor="codi-acces">{t('auth.codiAcces')}</label>
            <input
              id="codi-acces"
              type="text"
              className="entrada-codi__camp"
              value={codi}
              onChange={(e) => setCodi(e.target.value)}
              placeholder="EQ00-XXXX-XXXX"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
              autoFocus
            />
            <p className="form-help">{t('auth.codiAccesAjuda')}</p>
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={comprovant || !codi.trim()}>
            {comprovant ? t('auth.comprovant') : t('auth.entraAmbCodi')}
          </button>
        </form>
      </section>

      <div className="entrada-doble__separador"><span>{t('auth.oBe')}</span></div>

      <section className="entrada-doble__porta">
        <h2>{t('auth.loginTitle')}</h2>
        <p className="entrada-doble__ajuda">{t('auth.entraCompteIntro')}</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('auth.username')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn btn-secondary btn-block" disabled={loading}>
            {loading ? t('auth.loggingIn') : t('auth.loginButton')}
          </button>
        </form>

        <p className="entrada-doble__registre">
          {t('auth.dontHaveAccount')} <Link to="/register">{t('auth.registerHere')}</Link>
        </p>
      </section>
    </div>
  );
}

export default Login;
